import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { sendCode, verifyCode } from 'libs/api/case';
import { handleFieldError } from 'libs/api/errorHandle';
import {
  sendSMSVerification,
  sendSMSVerificationToFindUserId,
  receiveSMSVerification,
  receiveSMSVerificationToFindUserId,
  receiveSMSVerificationToUpdatePhone,
} from 'libs/api/user';
import { showNotification } from 'libs/utils';
import { TModalProps } from 'modules/types';
import { TSendSMS, TReceiveSMS } from 'modules/user';
import { Form, Input, Modal, Button, Row, Col } from 'antd';
import { FormWrapper } from 'GlobalStyles';

function SMSVerificationModal({ type, modalState, setModalState, setExpiredTime }: TModalProps) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [isSMSSent, setIsSMSSent] = useState(false);

  function handleCancel() {
    form.resetFields();
    setIsSMSSent(false);
    setModalState({ visible: false, record: '' });
  }

  //인증코드 전송 버튼
  function onClickSend() {
    form.validateFields().then((value) => {
      handleSubmit(value as TSendSMS);
    });

    async function handleSubmit(value: TSendSMS) {
      try {
        setSubmitButtonDisabled(true);
        if (type === 'case') {
          const { success } = await sendCode(value);
          if (success) {
            showNotification('success', '휴대전화로 인증코드를 보냈습니다.');
            setIsSMSSent(true);
          }
        } else {
          if (get(modalState, 'record.type', 'signup') === 'signup') {
            await sendSMSVerification(value);
          } else if (get(modalState, 'record.type', 'signup') === 'userid') {
            await sendSMSVerificationToFindUserId(value);
          } else {
            // phone edit
            await sendSMSVerification(value);
          }
          showNotification('success', '휴대전화로 인증코드를 보냈습니다.');
          setIsSMSSent(true);
        }
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  }

  //인증 확인 버튼
  function onClickVerify() {
    form.validateFields().then((value) => {
      handleSubmit(value as TReceiveSMS);
    });

    async function handleSubmit(value: TReceiveSMS) {
      try {
        setSubmitButtonDisabled(true);
        let response;
        if (type === 'case') {
          const { success } = await verifyCode(value);
          modalState.onFinish && modalState.onFinish(success);
          success && showNotification('success', '휴대전화 인증이 완료되었습니다.');

          setExpiredTime(
            moment()
              .add(1, 'hours')
              .toDate(),
          );
        } else if (get(modalState, 'record.type', 'signup') === 'signup') {
          response = await receiveSMSVerification(value);
          modalState.onFinish && modalState.onFinish(get(response, 'data.phone'));
          showNotification('success', '휴대전화 인증이 완료되었습니다.');
        } else if (get(modalState, 'record.type', 'signup') === 'userid') {
          response = await receiveSMSVerificationToFindUserId(value);
          modalState.onFinish && modalState.onFinish(get(response, 'data.userid'));
          showNotification('success', '휴대전화 인증이 완료되었습니다.');
        } else {
          // phone edit
          response = await receiveSMSVerificationToUpdatePhone(value);
          modalState.onFinish && modalState.onFinish(get(response, 'data.phone'));
          showNotification('success', '휴대전화 번호 수정이 완료되었습니다.');
        }

        handleCancel();
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  }

  useEffect(() => {
    setVisible(modalState.visible);
  }, [modalState.visible]);

  return (
    <Modal
      title={
        type === 'case'
          ? '본인의 휴대전화 번호로 인증해주세요'
          : get(modalState, 'record.type', 'phone') === 'phone'
          ? '새로운 휴대전화 번호 입력'
          : '휴대전화 인증'
      }
      visible={visible}
      centered={true}
      maskClosable={true}
      closable={true}
      footer={null}
      onCancel={handleCancel}
    >
      <FormWrapper style={{ padding: '0px' }}>
        <Form
          form={form}
          colon={false}
          className='form-inline'
          layout='vertical'
          initialValues={{ country_code: 82 }}
        >
          <Input.Group>
            <Row gutter={[8, 8]}>
              <Col flex='100px'>
                <Form.Item
                  name={'country_code'}
                  className='form-item'
                  label={<strong style={{ fontSize: '12px' }}>국가 코드</strong>}
                >
                  <Input prefix='+' style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col flex='auto'>
                <Form.Item
                  name={'phone'}
                  className='form-item'
                  label='휴대전화 번호'
                  getValueFromEvent={(event) => {
                    return event.target.value.replace(/[^\d]/g, '');
                  }}
                  rules={[{ required: true }]}
                >
                  <Input size='large' placeholder='휴대전화 번호를 입력해주세요(예시: 01012341234)' />
                </Form.Item>
              </Col>
            </Row>
          </Input.Group>
          {isSMSSent === true && (
            <Form.Item name={'code'} className='form-item' label='인증 코드'>
              <Input size='large' placeholder='인증 코드를 입력해주세요' />
            </Form.Item>
          )}
          <div>
            {isSMSSent === true ? (
              <Row justify='end' gutter={[8, 8]} style={{ marginBottom: '10px' }}>
                <Col>
                  <Button htmlType='submit' size='large' onClick={onClickSend} loading={submitButtonDisabled}>
                    재전송
                  </Button>
                </Col>
                <Col>
                  <Button
                    type='primary'
                    htmlType='submit'
                    size='large'
                    onClick={onClickVerify}
                    loading={submitButtonDisabled}
                  >
                    인증
                  </Button>
                </Col>
              </Row>
            ) : (
              <Button
                type='primary'
                htmlType='submit'
                size='large'
                onClick={onClickSend}
                loading={submitButtonDisabled}
                style={{ float: 'right' }}
              >
                전송
              </Button>
            )}
          </div>
        </Form>
      </FormWrapper>
    </Modal>
  );
}

SMSVerificationModal.defaultProps = {};

export default SMSVerificationModal;